import React from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Flex, Grid } from "antd"
import { routes } from "router/routes"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import EquipmentCalendar from "./EquipmentCalendar"
import DSTab from "ui/DSTab"
import { VideoPlayerModal } from "ui/VideoPlayerModal"
import { ConfirmModal } from "ui/ConfirmModal"
import RemoveExperienceWithBookingsModal from "ui/RemoveExperienceWithBookingsModal"
import AssignExperiencesModal from "ui/AssignExperiencesModal"
import AddEquipmentModal from "pagesV2/pro/Equipment/AddEquipmentModal"
import { useTranslation } from "react-i18next"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

// Local components
import HeaderSection from "./components/HeaderSection"
import BookingsSection from "./components/BookingsSection"
import DetailsCard from "./components/DetailsCard"
import ExperiencesCard from "./components/ExperiencesCard"
import GallerySection from "./components/GallerySection"
import DeactivateDeleteModals from "./components/DeactivateDeleteModals"

// Local hooks
import { useEquipmentDetailsData } from "./hooks/useEquipmentDetailsData"
import { useEquipmentDetailsModals } from "./hooks/useEquipmentDetailsModals"

// Local config
import { createMoreMenuItems, createTabItems } from "./config/menuConfig"

import styles from "./index.module.css"

const { useBreakpoint } = Grid

const EquipmentDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const screens = useBreakpoint()

  // Get active tab from URL search params
  const searchParams = new URLSearchParams(location.search)
  const activeTab = searchParams.get("tab") || "details"

  // Data hook
  const {
    equipment,
    equipmentData,
    equipmentDetails,
    upcomingBookings,
    totalBookingsCount,
    detailsLoading,
    boat,
    boatLoading,
    equipmentDeactivationDetails,
    equipmentDeletionDetails,
    deactivationDetailsLoading,
    deletionDetailsLoading,
    deactivateLoading,
    deleteLoading
  } = useEquipmentDetailsData(id)

  // Modal hook
  const modalHandlers = useEquipmentDetailsModals(
    id,
    equipment,
    {
      equipmentDeactivationDetails,
      equipmentDeletionDetails,
      deactivationDetailsLoading,
      deletionDetailsLoading,
      deactivateLoading,
      deleteLoading
    },
    t
  )

  // Handle tab change via URL navigation
  const handleTabChange = (tabKey) => {
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.set("tab", tabKey)
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true })
  }

  // Handle back navigation
  const handleBack = () => {
    navigate(routes.guideResourcesPath())
  }

  // Config items
  const menuItems = createMoreMenuItems(equipment?.is_active, t, hasPermission, MODULES, ACTIONS)
  const tabItems = createTabItems(t)

  // Show loading state
  if (detailsLoading && !equipmentDetails) {
    return <FirstLoading loading={detailsLoading} />
  }

  return (
    <div className={styles.container}>
      <HeaderSection
        equipment={equipment}
        equipmentData={equipmentData}
        screens={screens}
        onEdit={modalHandlers.handleEdit}
        onBack={handleBack}
        onMenuClick={modalHandlers.handleMenuClick}
        menuItems={menuItems}
      />

      <DSTab activeKey={activeTab} onChange={handleTabChange} items={tabItems} className={styles.tabs} />

      {activeTab === "details" && (
        <>
          <BookingsSection
            bookings={upcomingBookings}
            totalCount={totalBookingsCount}
            onAddBooking={modalHandlers.handleAddNewBooking}
            t={t}
          />

          <Flex gap={16} className={styles.detailsSection}>
            <DetailsCard description={equipment?.description} characteristics={equipment?.characteristics} t={t} />
            <ExperiencesCard
              experiences={equipment?.trips}
              totalCount={equipmentDetails?.metadata?.assigned_experiences_count || equipment?.trips?.length || 0}
              onAssign={modalHandlers.handleAssignExperiences}
              onRemove={modalHandlers.handleRemoveExperience}
              checkingConflicts={modalHandlers.checkingConflicts}
              t={t}
            />
          </Flex>

          <GallerySection media={equipment?.media} onMediaClick={modalHandlers.handleMediaClick} t={t} />
        </>
      )}

      {activeTab === "calendar" && <EquipmentCalendar equipmentId={id} equipment={equipment} />}

      {/* Modals */}
      {modalHandlers.showEquipmentModal && (
        <AddEquipmentModal
          visible={modalHandlers.showEquipmentModal}
          onCancel={modalHandlers.handleCancelEquipmentModal}
          record={boat}
          boatId={modalHandlers.modalBoatId}
          boatLoading={boatLoading}
          isDuplicateMode={modalHandlers.isDuplicateMode}
          onSubmit={modalHandlers.handleEquipmentSubmitData}
        />
      )}

      <DeactivateDeleteModals
        modalState={modalHandlers.deactivateDeleteModalState}
        onClose={modalHandlers.closeDeactivateDeleteModal}
        onConfirm={modalHandlers.handleDeactivateDeleteOk}
        deactivateLoading={modalHandlers.deactivateLoading}
        deleteLoading={modalHandlers.deleteLoading}
        deactivationDetailsLoading={modalHandlers.deactivationDetailsLoading}
        deletionDetailsLoading={modalHandlers.deletionDetailsLoading}
        t={t}
      />

      <VideoPlayerModal
        open={modalHandlers.videoPlayerOpen}
        selectedMedia={modalHandlers.selectedMedia}
        handleClose={modalHandlers.handleCloseVideoPlayer}
      />

      <AssignExperiencesModal
        open={modalHandlers.showAssignExperiencesModal}
        onCancel={modalHandlers.handleAssignExperiencesModalClose}
        onSave={modalHandlers.handleAssignExperiencesSave}
        currentExperiences={equipment?.trips || []}
        emptyText={t("equipment.no_experience_assigned", { defaultValue: "No experience assigned to this equipment." })}
      />

      <ConfirmModal
        open={modalHandlers.showRemoveConfirmModal}
        title={t("equipment.remove_experience", { defaultValue: "Remove Experience" })}
        info={t("equipment.confirm_remove_experience_text", {
          defaultValue: `Are you sure you want to remove <strong>${modalHandlers.experienceToRemoveName}</strong> from this equipment?`,
          experienceName: modalHandlers.experienceToRemoveName
        })}
        onCancel={modalHandlers.handleCancelRemove}
        onDelete={modalHandlers.handleConfirmRemove}
        deleteLabel={t("common.remove", { defaultValue: "Remove" })}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
        hideCancelButton={false}
      />

      <RemoveExperienceWithBookingsModal {...modalHandlers.assignConflictModalProps} resourceType="equipment" />

      <RemoveExperienceWithBookingsModal {...modalHandlers.removeExperienceWithBookingsModalProps} resourceType="equipment" />

      <RemoveExperienceWithBookingsModal {...modalHandlers.editConflictModalProps} resourceType="equipment" />
    </div>
  )
}

export default EquipmentDetails
